import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Switch,
  Stack,
  IconButton,
  Avatar,
  styled,
  Fade,
  Backdrop,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  Description,
  Payments,
  Settings,
  EventAvailable,
  Diamond,
  CheckCircle,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 1000,
  height: 800,
  bgcolor: '#fff',
  borderRadius: '40px',
  display: 'flex',
  overflow: 'hidden',
  boxShadow: '0px 40px 100px rgba(27, 37, 75, 0.15)',
};

const SidebarStyle = {
  width: 320,
  bgcolor: '#F8F9FE',
  p: 5,
  borderRight: '1px solid #E9EDF7',
  display: 'flex',
  flexDirection: 'column',
};
const ContentStyle = {
  flex: 1,
  p: 8,
  display: 'flex',
  flexDirection: 'column',
  bgcolor: '#fff',
  overflowY: 'auto',
};

const StyledInput = styled(TextField)(({ error }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F9FAFB',
    borderRadius: '16px',
    '& fieldset': { border: error ? '2px solid #EE5D50' : '1px solid #E5E7EB' },
    '&:hover fieldset': {
      border: error ? '2px solid #EE5D50' : '1px solid #4318FF',
    },
  },
}));

const steps = [
  { label: 'Prestation', desc: 'Détails du service', icon: <Description /> },
  { label: 'Tarification', desc: 'Prix et accès', icon: <Payments /> },
  {
    label: 'Configuration',
    desc: 'Capacité et intervenant',
    icon: <EventAvailable />,
  },
  { label: 'Horaires', desc: 'Dates des sessions', icon: <Settings /> },
];

const WorkshopModal = ({
  open,
  handleClose,
  refreshData,
  intervenantId,
  initialData = null,
}) => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [intervenants, setIntervenants] = useState([]);
  const [selectedIntervenant, setSelectedIntervenant] = useState('');
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    title_wss: '',
    description_wss: '',
    is_paid_wss: false,
    capacity_ws: 10,
    duration_wss: '60',
    price_wss: 0,
    sessions: [
      {
        id_wss: null,
        date_wss: '',
        capacity: 10,
        is_paid_wss: false,
        is_published_wss: true,
      },
    ],
    is_published_wss: true,
    nbr_wss: 1,
    repeat_wss: 'weekly',
    format_ws: 'zoom',
    is_accessible_participant: true,
    photo_wss: null,
    // 📍 AJOUT ICI
    address_ws: '',
    city_ws: '',
    postal_code_ws: '',
    country_ws: '',
    lat_ws: '',
    lng_ws: '',
  });

  // Charger les intervenants pour le menu déroulant
  const fetchIntervenants = useCallback(async () => {
    if (Number(user?.role) === 3) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/intervenant/l/list_intervenant`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIntervenants(res.data.result || []);
    } catch (err) {
      console.error(err);
    }
  }, [user?.role, token]);

  useEffect(() => {
    if (open) {
      fetchIntervenants();
      if (initialData) {
        setFormData({
          title_wss: initialData.title_ws || '',
          description_wss: initialData.description_ws || '',
          is_paid_wss: !!initialData.is_paid_ws,
          capacity_ws: initialData.capacity_ws || 10,
          duration_wss: initialData.duration_ws || '60',
          price_wss: initialData.price_ws || 0,
          sessions:
            initialData.sessions?.map((s) => ({
              id_wss: s.id_wss,
              date_wss: s.date_wss,
              capacity: s.capacity,
              is_paid_wss: s.is_paid_wss,
              is_published_wss: s.is_published_wss,
            })) || [],
          nbr_wss: initialData.nbr_ws || 1,
          repeat_wss: initialData.repeat_ws || 'weekly',
          format_ws: initialData.format_ws || 'zoom',
          is_accessible_participant: !!initialData.is_accessible_participant,
          photo_wss: initialData.photo_ws || null,
          is_published_wss: !!initialData.is_published_ws,
          address_ws: initialData.address_ws || '',
          city_ws: initialData.city_ws || '',
          postal_code_ws: initialData.postal_code_ws || '',
          country_ws: initialData.country_ws || '',
          lat_ws: initialData.lat_ws || '',
          lng_ws: initialData.lng_ws || '',
        });
        setSelectedIntervenant(initialData.id_intervenant_ws || '');
      } else {
        setSelectedIntervenant(intervenantId || '');
        setFormData({
          title_wss: '',
          description_wss: '',
          is_paid_wss: false,
          capacity_ws: 10,
          duration_wss: '60',
          price_wss: 0,
          sessions: [
            {
              id_wss: null,
              date_wss: '',
              capacity: 10,
              is_paid_wss: false,
              is_published_wss: true,
            },
          ],
          is_published_wss: true,
          nbr_wss: 1,
          repeat_wss: 'weekly',
          format_ws: 'zoom',
          is_accessible_participant: true,
          photo_wss: null,
          address_ws: '',
          city_ws: '',
          postal_code_ws: '',
          country_ws: '',
          lat_ws: '',
          lng_ws: '',
        });
      }
      setActiveStep(0);
      setErrors({});
    }
  }, [open, initialData, intervenantId, fetchIntervenants]);

  const validateStep = () => {
    let newErrors = {};
    if (activeStep === 0) {
      if (!formData.title_wss) newErrors.title_wss = true;
      if (!formData.description_wss) newErrors.description_wss = true;
      // Si Admin, l'intervenant est obligatoire
      if (Number(user?.role) !== 3 && !selectedIntervenant)
        newErrors.selectedIntervenant = true;
    }
    if (
      activeStep === 1 &&
      formData.is_paid_wss &&
      (!formData.price_wss || formData.price_wss <= 0)
    )
      newErrors.price_wss = true;
    if (
      activeStep === 2 &&
      (!formData.capacity_ws || formData.capacity_ws <= 0)
    )
      newErrors.capacity_ws = true;
    if (activeStep === 3)
      formData.sessions.forEach((s, i) => {
        if (!s.date_wss) newErrors[`date_${i}`] = true;
      });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };const handleSubmit = async () => {
  if (!validateStep()) return;
  setLoading(true);

  const payload = {
    ...formData,
      session_date_wss: formData.sessions
    .map(s => s.date_wss)
    .filter(Boolean),
    is_paid_wss: formData.is_paid_wss ? 1 : 0,
    is_published_wss: formData.is_published_wss ? 1 : 0,
    is_accessible_participant: formData.is_accessible_participant ? 1 : 0,
    photo_wss: formData.photo_wss, // ⚠️ géré à part si fichier
  };

  try {
    const path = user?.role === 3 ? 'intervenant' : 'admin';

    const url = initialData
      ? `${process.env.REACT_APP_API_URL}/${path}/atelier/a/update/test/${initialData.id_ws}`
      : `${process.env.REACT_APP_API_URL}/${path}/atelier/meet/c/create/${selectedIntervenant}`;

    const res = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    toast.success(res.data.message || 'Opération réussie !');
    refreshData();
    handleClose();

  } catch (e) {
    const apiData = e.response?.data;

    if (apiData?.errors) {
      const firstError = Object.values(apiData.errors).flat()[0];
      toast.error(firstError);
    } else if (apiData?.message) {
      toast.error(apiData.message);
    } else {
      toast.error('Une erreur serveur est survenue.');
    }

  } finally {
    setLoading(false);
  }
};

  return (
    <Modal
      open={open}
      onClose={handleClose}
      BackdropComponent={Backdrop}
      BackdropProps={{ sx: { backdropFilter: 'blur(12px)' } }}
    >
      <Fade in={open}>
        <Box sx={ModalStyle}>
          <Box sx={SidebarStyle}>
            <Typography variant="h6" sx={{ fontWeight: '900', mb: 5 }}>
              Atelier
            </Typography>
            {steps.map((step, i) => (
              <Stack
                key={i}
                direction="row"
                spacing={3}
                alignItems="center"
                sx={{ mb: 5 }}
              >
                <Avatar
                  sx={{
                    bgcolor:
                      activeStep >= i
                        ? activeStep === i
                          ? '#4318FF'
                          : '#05CD99'
                        : '#fff',
                    color: activeStep >= i ? '#fff' : '#A3AED0',
                    borderRadius: '18px',
                    border: '1px solid #E9EDF7',
                  }}
                >
                  {activeStep > i ? <CheckCircle /> : step.icon}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: '800', fontSize: '14px' }}>
                    {step.label}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#A3AED0' }}>
                    {step.desc}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Box>

          <Box sx={ContentStyle}>
            <Stack direction="row" justifyContent="space-between" mb={4}>
              <Typography variant="h4" sx={{ fontWeight: '900' }}>
                {steps[activeStep].label}
              </Typography>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Stack>

            <Box sx={{ flexGrow: 1 }}>
              {activeStep === 0 && (
                <Stack spacing={3}>
                  {/* LISTE INTERVENANTS POUR ADMIN */}
                  {[1, 2].includes(Number(user?.role)) && (
                    <StyledInput
                      fullWidth
                      select
                      label="Choisir un intervenant"
                      error={errors.selectedIntervenant}
                      value={selectedIntervenant}
                      onChange={(e) => {
                        setSelectedIntervenant(e.target.value);
                        setErrors({ ...errors, selectedIntervenant: false });
                      }}
                    >
                      {intervenants.map((i) => (
                        <MenuItem key={i.id_user} value={i.id_user}>
                          {i.prenom_user} {i.nom_user}
                        </MenuItem>
                      ))}
                    </StyledInput>
                  )}
                  <StyledInput
                    fullWidth
                    placeholder="Titre"
                    error={errors.title_wss}
                    value={formData.title_wss}
                    onChange={(e) => {
                      setFormData({ ...formData, title_wss: e.target.value });
                      setErrors({ ...errors, title_wss: false });
                    }}
                  />
                  <StyledInput
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Description"
                    error={errors.description_wss}
                    value={formData.description_wss}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        description_wss: e.target.value,
                      });
                      setErrors({ ...errors, description_wss: false });
                    }}
                  />
                  <Grid container spacing={4}>
                    <Grid item xs={6}>
                      <StyledInput
                        fullWidth
                        type="number"
                        label="Durée (min)"
                        value={formData.duration_wss}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            duration_wss: e.target.value,
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ py: 2, borderStyle: 'dashed' }}
                  >
                    <CloudUpload sx={{ mr: 1 }} />{' '}
                    {formData.photo_wss
                      ? 'Image sélectionnée'
                      : 'Photo de couverture'}
                    <input
                      type="file"
                      hidden
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          photo_wss: e.target.files[0],
                        })
                      }
                    />
                  </Button>
                  {/* Affichage de la miniature */}
                  {formData.photo_wss && (
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Aperçu de la photo :
                      </Typography>
                      <Box
                        component="img"
                        sx={{
                          height: 100,
                          width: 100,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid #ddd',
                          boxShadow: 1,
                        }}
                        alt="Aperçu"
                        src={
                          typeof formData.photo_wss === 'string'
                            ? `${process.env.REACT_APP_API_URL}/storage/workshop/${formData.photo_wss}`
                            : URL.createObjectURL(formData.photo_wss)
                        }
                      />
                    </Box>
                  )}
                </Stack>
              )}

              {activeStep === 1 && (
                <Stack spacing={4}>
                  {/* Switch Payant existant */}
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: '#F9FAFB',
                      borderRadius: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography sx={{ fontWeight: '800' }}>
                      Atelier Payant
                    </Typography>
                    <Switch
                      checked={formData.is_paid_wss}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_paid_wss: e.target.checked,
                        })
                      }
                    />
                  </Box>
                  {formData.is_paid_wss && (
                    <StyledInput
                      fullWidth
                      type="number"
                      label="Prix (€)"
                      value={formData.price_wss}
                      onChange={(e) =>
                        setFormData({ ...formData, price_wss: e.target.value })
                      }
                    />
                  )}

                  {/* ✨ NOUVEAU : Switch Accès Abonné / Participant */}
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: '#F9FAFB',
                      borderRadius: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Diamond sx={{ color: '#4318FF' }} />
                      <Box>
                        <Typography sx={{ fontWeight: '800' }}>
                          Accès Abonnés VIP
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#A3AED0' }}>
                          Permettre aux abonnés d'accéder gratuitement
                        </Typography>
                      </Box>
                    </Stack>
                    <Switch
                      checked={formData.is_accessible_participant}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_accessible_participant: e.target.checked,
                        })
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: '#F9FAFB',
                      borderRadius: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CheckCircle
                        sx={{
                          color: formData.is_published_wss
                            ? '#05CD99'
                            : '#A3AED0',
                        }}
                      />
                      <Box>
                        <Typography sx={{ fontWeight: '800' }}>
                          Publication de l’atelier
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#A3AED0' }}>
                          Rendre l’atelier visible pour les participants
                        </Typography>
                      </Box>
                    </Stack>

                    <Switch
                      checked={formData.is_published_wss}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_published_wss: e.target.checked,
                        })
                      }
                    />
                  </Box>
                </Stack>
              )}

              {activeStep === 2 && (
                <Stack spacing={4}>
                  <StyledInput
                    fullWidth
                    type="number"
                    label="Capacité Maximale"
                    error={errors.capacity_ws}
                    value={formData.capacity_ws}
                    onChange={(e) => {
                      setFormData({ ...formData, capacity_ws: e.target.value });
                      setErrors({ ...errors, capacity_ws: false });
                    }}
                  />
                  {/*  <StyledInput fullWidth select label="Récurrence" value={formData.repeat_wss} onChange={e => setFormData({...formData, repeat_wss: e.target.value})}>
                    <MenuItem value="daily">Quotidien</MenuItem><MenuItem value="weekly">Hebdomadaire</MenuItem><MenuItem value="monthly">Mensuel</MenuItem>
                  </StyledInput> */}
                  <Grid item xs={12}>
                    <StyledInput
                      fullWidth
                      select
                      label="Format"
                      value={formData.format_ws}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          format_ws: e.target.value,
                        })
                      }
                      // Permet d'afficher le label correctement même si la valeur est vide
                      SelectProps={{
                        displayEmpty: true,
                      }}
                    >
                      {/* Option par défaut moderne */}
                      <MenuItem value="" disabled>
                        <em>Sélectionnez un format...</em>
                      </MenuItem>

                      <MenuItem value="zoom">
                        <span style={{ marginRight: '8px' }}>💻</span> Zoom
                      </MenuItem>

                      <MenuItem value="en_presentiel">
                        <span style={{ marginRight: '8px' }}>📍</span>{' '}
                        Présentiel
                      </MenuItem>
                    </StyledInput>
                  </Grid>
                  {formData.format_ws === 'en_presentiel' && (
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <StyledInput
                          fullWidth
                          label="Adresse"
                          value={formData.address_ws || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              address_ws: e.target.value,
                            })
                          }
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <StyledInput
                          fullWidth
                          label="Ville"
                          value={formData.city_ws || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              city_ws: e.target.value,
                            })
                          }
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <StyledInput
                          fullWidth
                          label="Code postal"
                          value={formData.postal_code_ws || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              postal_code_ws: e.target.value,
                            })
                          }
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <StyledInput
                          fullWidth
                          label="Pays"
                          value={formData.country_ws || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              country_ws: e.target.value,
                            })
                          }
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <StyledInput
                          fullWidth
                          label="Latitude"
                          value={formData.lat_ws || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              lat_ws: e.target.value,
                            })
                          }
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <StyledInput
                          fullWidth
                          label="Longitude"
                          value={formData.lng_ws || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              lng_ws: e.target.value,
                            })
                          }
                        />
                      </Grid>
                    </Grid>
                  )}
                </Stack>
              )}

              {activeStep === 3 && (
                <Stack spacing={4}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: '16px' }}
                  >
                    <Typography sx={{ fontWeight: '800' }}>
                      Sessions : {formData.nbr_wss}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        onClick={() =>
                          setFormData({
                            ...formData,
                            nbr_wss: Math.max(1, formData.nbr_wss - 1),
                            sessions: formData.sessions.slice(0, -1),
                          })
                        }
                      >
                        <RemoveIcon />
                      </IconButton>
                      <IconButton
                        onClick={() =>
                          setFormData({
                            ...formData,
                            nbr_wss: formData.nbr_wss + 1,
                            sessions: [
                              ...formData.sessions,
                              {
                                id_wss: null,
                                date_wss: '',
                                capacity: formData.capacity_ws,
                                is_paid_wss: formData.is_paid_wss,
                                is_published_wss: true,
                              },
                            ],
                          })
                        }
                        sx={{ bgcolor: '#4318FF', color: '#fff' }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                  {formData.sessions.map((session, i) => (
                    <StyledInput
                      key={i}
                      fullWidth
                      type="datetime-local"
                      error={errors[`date_${i}`]}
                      value={session.date_wss
                        ?.replace(' ', 'T')
                        .substring(0, 16)}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => {
                        const val = e.target.value;

                        const updated = [...formData.sessions];
                        updated[i].date_wss = val.replace('T', ' ') + ':00';

                        setFormData({ ...formData, sessions: updated });
                        setErrors({ ...errors, [`date_${i}`]: false });
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <Stack direction="row" justifyContent="space-between" mt={4}>
              <Button
                onClick={() => setActiveStep((prev) => prev - 1)}
                disabled={activeStep === 0}
              >
                Revenir en arrière
              </Button>
              <Button
                variant="contained"
                disabled={loading}
                onClick={
                  activeStep === 3
                    ? handleSubmit
                    : () =>
                        validateStep()
                          ? setActiveStep((prev) => prev + 1)
                          : toast.error('Champs manquants')
                }
                sx={{ bgcolor: '#4318FF', borderRadius: '12px', px: 6 }}
              >
                {activeStep === 3 ? 'Confirmer' : 'Suivant'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default WorkshopModal;
